import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Grid, TablePagination } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '~/components/Typography';
import { Pagination } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useUploadDispatch } from '~/contexts/upload';
import type { ContentKind, MediaItem } from '~/generated/graphql';
import { useLastFolder } from '~/hooks/folders';
import { usePollInterval } from '~/hooks/polling';
import UploadMediaIllustration from '~/images/illustrations/upload-media.svg';
import { mediaDimensions } from '~/lib/media';
import { PER_PAGE } from '~/lib/pagination';
import { ContentSelectDocument, ContentSelectSearchDocument } from '../index.generated';
import { ActionsContainer, FolderCardSmall, MediaItemCardSmall, UploadButton } from './';

export interface SmallGridProps {
  kind: Array<ContentKind>;
  onClick: (
    id: number,
    src: string,
    type: 'image' | 'video',
    dimensions: [number, number],
    format: string,
  ) => void;
  rememberLastFolder?: boolean;
  storageContext?: string;
}

export const SmallGrid = ({
  kind,
  onClick,
  rememberLastFolder = false,
  storageContext = 'media',
}: SmallGridProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree, landingFolderId } = useFolderContext();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);

  const uploadDispatch = useUploadDispatch();
  const { pollInterval, setFastPolling } = usePollInterval(0, 1_000);

  const [contentFolderId, setContentFolderId] = useLastFolder({
    enabled: rememberLastFolder,
    defaultFolderId: landingFolderId,
    storageContext,
  });

  const changeFolder = (id: number) => {
    setPage(1);
    setContentFolderId(id);
  };

  const { data, startPolling, stopPolling } = useQuery(ContentSelectDocument, {
    variables: {
      condition: { kind },
      contentFolderId,
      networkId: currentNetwork.id,
      page,
      perPage,
    },
  });

  const [searchContent, { data: searchData }] = useLazyQuery(ContentSelectSearchDocument, {
    variables: {
      condition: { kind, search },
      page,
      perPage,
      networkId: currentNetwork.id,
    },
  });

  useEffect(() => {
    startPolling(pollInterval);
    return () => stopPolling();
  }, [pollInterval, startPolling, stopPolling]);

  useEffect(() => {
    const listener = () => setFastPolling(30);
    uploadDispatch({ type: 'addListener', listener });
    return () => uploadDispatch({ type: 'removeListener', listener });
  }, [setFastPolling, uploadDispatch]);

  const contentFolder = data?.network?.contentFolder;
  const folders = search ? [] : contentFolder?.children ?? [];
  const items = useMemo(() => {
    const content =
      (search ? searchData?.network?.content.nodes : contentFolder?.content.nodes) ?? [];
    return content.filter((x): x is MediaItem => x.__typename === 'MediaItem');
  }, [contentFolder?.content.nodes, search, searchData?.network?.content.nodes]);
  const totalCount =
    (search
      ? searchData?.network?.content.pageInfo.nodesCount
      : contentFolder?.content.pageInfo.nodesCount) ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}>
      <Box>
        <ActionsContainer
          sx={{ borderBottom: '1px solid rgba(0,0,0,0.15)', px: 1, pt: 1, pb: 2, marginTop: 0 }}
        >
          <SearchBar
            onChange={(term) => {
              setPage(1);
              setSearch(term);
              void searchContent();
            }}
            placeholder={t('components.media_select.labels.search_placeholder')}
            search={search}
            sx={{ fontSize: '14px !important', mr: 1, paddingX: 0.5, paddingY: 1.5 }}
          />
          <UploadButton disabled={!contentFolder?.canUpdate.value} folderId={contentFolderId} />
        </ActionsContainer>

        {!search && contentFolder && (
          <Box sx={{ p: 1 }}>
            <FolderBreadcrumbs
              allItems={() => changeFolder(folderTree.id)}
              changeFolder={changeFolder}
              folder={contentFolder}
              label={t('components.media_select.labels.all_items')}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1, height: '100%', overflowY: 'auto' }}>
        {folders.length === 0 && items.length === 0 ? (
          <EmptyState
            illustration={UploadMediaIllustration}
            header={t('pages.content.empty_state.header')}
            description={t('pages.content.empty_state.description')}
          />
        ) : (
          <>
            {folders.length > 0 && (
              <Box
                sx={{
                  paddingBottom: 2,
                  borderBottom: items.length > 0 ? 1 : 0,
                  borderBottomColor: 'borders.main',
                }}
              >
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {t('pages.studio.edit.settings.media_select.folders_plural', {
                    count: folders.length,
                  })}
                </Typography>
                <Grid container spacing={1}>
                  {folders.map((folder) => (
                    <FolderCardSmall
                      folder={folder}
                      key={folder.id}
                      onClick={() => changeFolder(folder.id)}
                    />
                  ))}
                </Grid>
              </Box>
            )}
            {items.length > 0 && (
              <Box sx={{ paddingTop: folders.length > 0 ? 2 : 0, paddingBottom: 2 }}>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {t('pages.studio.edit.settings.media_select.items_plural', {
                    count: items.length,
                  })}
                </Typography>
                <Grid container spacing={1}>
                  {items.map((mediaItem) => (
                    <MediaItemCardSmall
                      key={mediaItem.id}
                      mediaItem={mediaItem}
                      onClick={() =>
                        onClick(
                          mediaItem.id,
                          mediaItem.originalFile.uri,
                          mediaItem.kind === 'IMAGE' ? 'image' : 'video',
                          mediaDimensions(mediaItem),
                          mediaItem.format ?? '',
                        )
                      }
                    />
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <TablePagination
          ActionsComponent={Pagination}
          component="div"
          count={totalCount}
          labelRowsPerPage=""
          labelDisplayedRows={() => ''}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setPerPage(parseInt(event.target.value));
            setPage(1);
          }}
          page={page - 1}
          rowsPerPage={perPage}
          rowsPerPageOptions={[25, 50, 100]}
          size="small"
          sx={{
            '.MuiTablePagination-toolbar': {
              minHeight: 40,
              px: 1,
              justifyContent: 'space-between',
            },
            '.MuiTablePagination-spacer': { display: 'none' },
            '.MuiTablePagination-displayedRows': { display: 'none' },
            '.MuiTablePagination-selectLabel': { display: 'none' },
            '.MuiTablePagination-input': { mr: 'auto' },
          }}
        />
      </Box>
    </Box>
  );
};
